import { Box, Card, Typography, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { getSensorLabel } from '../utils/sensorLabels';

/**
 * ModificationAlert
 * Props:
 *  - alertActivate: object mapping keys to boolean (active?)
 *  - setAlertActivate: setter for alertActivate
 *  - alertValue: object mapping keys to number or [min, max]
 *  - setAlertValue: setter for alertValue
 */
const ModificationAlert = ({
  alertActivate,
  setAlertActivate,
  alertValue,
  setAlertValue
}) => {
  const metrics = Object.keys(alertValue);

  const handleActivateToggle = (key) => (e) => {
    setAlertActivate(prev => ({ ...prev, [key]: e.target.checked }));
  };

  // Handle both single and range values
  const handleValueChange = (key, idx = null) => (e) => {
    const raw = e.target.value;
    let val = raw === '' ? '' : Number(raw);
    // Determine if range 
    const current = alertValue[key];
    if (Array.isArray(current)) {
      // Copy existing range or default [0,0]
      const range = [...current];
      // idx: 0 for min, 1 for max
      range[idx] = val;
      // Ensure min <= max
      if (range[0] !== '' && range[1] !== '' && range[0] > range[1]) {
        if (idx === 0) range[1] = range[0];
        else range[0] = range[1];
      }
      setAlertValue(prev => ({ ...prev, [key]: range }));
    } else {
      // Single-value metric
      setAlertValue(prev => ({ ...prev, [key]: val }));
    }
  };

  // clamp input props based on metric
  const getInputProps = (key) => {
    if (key === 'humidity' || key === 'groundHumidity') {
      return { min: 0, max: 100 };
    }
    if (key === 'temperature') {
      return { min: -50, max: 100 };
    }
    return { min: 0 };
  };

  return (
    <Box
      component="section"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
        gap: 2,
        p: 2,
        bgcolor: '#1a1b2e',
        borderRadius: 2,
      }}
    >
      {metrics.map(key => {
        const value = alertValue[key];
        const isRange = Array.isArray(value);
        const inputProps = getInputProps(key);
        return (
          <Card
            key={key}
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: '#2E2F45',
              color: '#EAEAEA',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderColor: alertActivate[key] ? '#3b82f6' : '#555',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {getSensorLabel(key)}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={alertActivate[key]}
                  onChange={handleActivateToggle(key)}
                  size="small"
                  sx={{ color: '#888', '&.Mui-checked': { color: '#3b82f6' } }}
                />
              }
              label="Activé"
              sx={{ color: '#EAEAEA' }}
            />

            {isRange ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Min"
                  type="number"
                  size="small"
                  value={value[0]}
                  onChange={handleValueChange(key, 0)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={inputProps}
                  sx={{ flex: 1, '& .MuiInputBase-input': { color: '#EAEAEA', py: 0.5 } }}
                />
                <TextField
                  label="Max"
                  type="number"
                  size="small"
                  value={value[1]}
                  onChange={handleValueChange(key, 1)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={inputProps}
                  sx={{ flex: 1, '& .MuiInputBase-input': { color: '#EAEAEA', py: 0.5 } }}
                />
              </Box>
            ) : (
              <TextField
                label="Seuil"
                type="number"
                size="small"
                value={value}
                onChange={handleValueChange(key)}
                InputLabelProps={{ shrink: true }}
                inputProps={inputProps}
                sx={{ '& .MuiInputBase-input': { color: '#EAEAEA', py: 0.5 } }}
              />
            )}
          </Card>
        );
      })}
    </Box>
  );
};

export default ModificationAlert;
